import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useContacts } from '../../hooks/useContacts';
import { contactsService } from '../../services/contactsService';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ResponsiveTable from '../../components/ui/ResponsiveTable';
import Card from '../../components/ui/Card';

const ContactsList: React.FC = () => {
  const { contacts, loading, error, refreshContacts } = useContacts();
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const navigate = useNavigate();

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.contact_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contact.email && contact.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (contact.phone && contact.phone.includes(searchTerm));
    
    const matchesFilter = filterType === 'all' || contact.type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        const { error } = await contactsService.deleteContact(id);
        if (error) {
          alert(error.message);
        } else {
          refreshContacts();
        }
      } catch (err) {
        alert('Failed to delete contact');
      }
    }
  };

  // Show loading while authentication is in progress or business is not loaded
  if (authLoading || (isAuthenticated && !businessId)) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <p className="text-gray-600">
          {authLoading ? 'Loading authentication...' : 'Loading business data...'}
        </p>
      </div>
    );
  }

  // Show message if user is authenticated but has no business
  if (isAuthenticated && !businessId) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Business Profile Required</h2>
          <p className="text-gray-600 mb-4">
            You need to create a business profile before you can manage contacts.
          </p>
          <p className="text-sm text-gray-500">
            Please complete your business setup first.
          </p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">
            Please log in to access the contact management system.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        {error}
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Contacts', current: true }
  ];

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
        <Link to="/contacts/new">
          <Button variant="primary">
            Add New Contact
          </Button>
        </Link>
      </div>

      <div className="mb-6 space-y-4">
        <div className="max-w-md">
          <SearchInput
            placeholder="Search contacts by name, ID, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'All Types' },
            { value: 'customer', label: 'Customers' },
            { value: 'vendor', label: 'Vendors' },
            { value: 'employee', label: 'Employees' },
            { value: 'other', label: 'Other' }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilterType(tab.value)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                filterType === tab.value
                  ? 'bg-primary-100 text-primary-700 border border-primary-300'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <ResponsiveTable
          headers={['Profile', 'Contact ID', 'Name', 'Type', 'Email', 'Phone', 'Balance']}
          rows={filteredContacts.map(contact => [
            contact.profile_picture_url ? (
              <img
                src={contact.profile_picture_url}
                alt={contact.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                <span className="text-sm font-medium text-gray-700">
                  {contact.name.charAt(0).toUpperCase()}
                </span>
              </div>
            ),
            contact.contact_id,
            contact.name,
            <span className="badge badge-info">
              {contactsService.getContactTypeLabel(contact.type)}
            </span>,
            contact.email || '-',
            contact.phone || '-',
            contactsService.getFormattedBalance(contact.balance)
          ])}
          renderRow={(row, index) => {
            const contact = filteredContacts[index];
            return (
              <tr
                key={contact.id}
                onClick={() => navigate(`/contacts/${contact.id}`)}
                className="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  {row[0]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row[1]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row[2]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[3]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[4]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[5]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[6]}
                </td>
              </tr>
            );
          }}
          renderMobileCard={(row, index) => {
            const contact = filteredContacts[index];
            return (
              <Card
                key={contact.id}
                onClick={() => navigate(`/contacts/${contact.id}`)}
                className="mb-4"
              >
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {row[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {row[2]}
                        </h3>
                        <span className="text-sm font-medium text-gray-500">
                          {row[6]}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Contact ID</p>
                          <p className="text-sm font-medium text-gray-900">{row[1]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Type</p>
                          <p className="text-sm font-medium text-gray-900">{row[3]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Email</p>
                          <p className="text-sm font-medium text-gray-900">{row[4]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Phone</p>
                          <p className="text-sm font-medium text-gray-900">{row[5]}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          }}
          emptyMessage="No contacts found"
        />
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing {filteredContacts.length} of {contacts.length} contacts
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" disabled>
            Previous
          </Button>
          <Button variant="outline" disabled>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContactsList;